/**
 * Step 1 of making a contribution: the bank account the client will send from (S-27).
 *
 * The destination account and the contribution reference are not shown before this one is
 * registered; the server holds them back, and this card is where the client does it, on the
 * page, without a detour to the profile. Missing: the form is already open. Done: the account
 * the client will send from, with the way to add another.
 *
 * What the client is told here is true of the whole flow: contributions come from an account in
 * their name, and money we send back to them goes to the account it came from.
 */
import { useCallback, useEffect, useState } from 'react';
import bankAccountsService, { type ClientBankAccount } from '../../services/bankAccounts.service';
import { SOURCE_ACCOUNT_NOTICE, accountTypeForCurrency } from '../../pages/client/fund.helpers';
import { colors } from '../../styles/theme';
import BankAccountSelector from './BankAccountSelector';

interface Props {
  readonly clientId: string;
  /** Currency of the series the client is about to send to. */
  readonly currency: string;
  /** Told once an account is saved, so the page asks the server for the instructions again. */
  readonly onRegistered: () => void;
}

export default function SourceAccountCard({ clientId, currency, onRegistered }: Props): JSX.Element {
  const [accounts, setAccounts] = useState<ClientBankAccount[] | null>(null);
  const [addingAnother, setAddingAnother] = useState(false);

  const load = useCallback(async () => {
    try {
      setAccounts(await bankAccountsService.listForClient(clientId));
    } catch {
      setAccounts([]);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  if (accounts === null) {
    return <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  }

  const usable = accounts.filter((a) => a.currency_code === currency);
  const registered = (): void => {
    setAddingAnother(false);
    load();
    onRegistered();
  };

  if (usable.length === 0) {
    return (
      <section
        className="rounded-2xl p-5 mb-4"
        style={{ background: colors.warningBg, border: `1px solid ${colors.warning}` }}
      >
        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: colors.inkSoft }}>
          Step 1 of 2
        </p>
        <h2 className="text-sm font-semibold" style={{ color: colors.ink }}>
          The account you&apos;ll send from
        </h2>
        <p className="text-xs mt-1 mb-3" style={{ color: colors.inkSoft }}>
          Your contributions are sent from a bank account in your name, and any money we send back
          to you goes to that same account. Add it once and use it every time.
        </p>
        <BankAccountSelector
          clientId={clientId}
          hideSelection
          startOpen
          fixedType={accountTypeForCurrency(currency)}
          saveLabel="Save and continue"
          onSelectionChange={(accountId) => { if (accountId) registered(); }}
        />
        <p className="text-xs mt-3" style={{ color: colors.inkSoft }}>
          The account must be in your name. If it is in another name, talk to your Account
          Executive.
        </p>
        <p className="text-xs mt-1" style={{ color: colors.muted }}>
          Your browser will ask to share your location. Allow it to save the account.
        </p>
        <p className="text-xs mt-3" style={{ color: colors.inkSoft }}>{SOURCE_ACCOUNT_NOTICE}</p>
      </section>
    );
  }

  const first = usable[0];
  return (
    <section
      className="rounded-2xl p-5 mb-4"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: colors.inkSoft }}>
        Step 1 of 2
      </p>
      <p className="text-sm font-semibold" style={{ color: colors.ink }}>
        You&apos;ll send from {first.nickname || first.holder_name} ····{first.account_number_last4}
      </p>
      <p className="text-xs mt-1" style={{ color: colors.muted }}>
        You can also send from any other account registered in your name.
      </p>
      {addingAnother ? (
        <BankAccountSelector
          clientId={clientId}
          hideSelection
          startOpen
          fixedType={accountTypeForCurrency(currency)}
          onSelectionChange={(accountId) => { if (accountId) registered(); }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setAddingAnother(true)}
          className="text-sm px-3 py-1.5 rounded-lg mt-3"
          style={{ background: colors.canvas, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Add another account
        </button>
      )}
    </section>
  );
}
